import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from 'react';
import AdminLayout from 'Components/Admin/AdminLayout';
import { Container, Row, Col, Card, CardHeader, CardBody, Form, FormGroup, Label, Input, Button, Table, Alert } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faUser, faBuilding, faSave, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
const API_BASE = window.HMI_API_BASE || process.env.REACT_APP_API_URL || 'https://api.heresmyinfo.com/api';
export default function Subscriptions() {
  const {
    t
  } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({
    users: [],
    businesses: []
  });
  const [selectedClient, setSelectedClient] = useState(null); // { id, name, email/state, type: 'user' | 'business' }

  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]); // Subscription Form State

  const [clientSub, setClientSub] = useState({
    plan_id: '',
    price_override: '',
    billing_cycle: 'monthly',
    status: 'active',
    addons: [] // list of { addon_id, price_override, quantity }

  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const token = localStorage.getItem('token') || ''; // Load plans & addons reference lists

  useEffect(() => {
    const headers = {
      'Authorization': `Bearer ${token}`
    };
    Promise.all([fetch(`${API_BASE}/admin/plans`, {
      headers
    }).then(r => r.json()), fetch(`${API_BASE}/admin/addons`, {
      headers
    }).then(r => r.json())]).then(([plansRes, addonsRes]) => {
      if (plansRes.success) setPlans(plansRes.data);
      if (addonsRes.success) setAddons(addonsRes.data);
    }).catch(err => {
      console.error(err);
      setError('Failed to load subscription reference lists');
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = async e => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/admin/clients?query=${encodeURIComponent(searchQuery)}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }).then(r => r.json());

      if (res.success) {
        setSearchResults(res.data);

        if (res.data.users.length === 0 && res.data.businesses.length === 0) {
          setError('No users or businesses found matching search query');
        }
      } else {
        setError(res.error?.message || 'Error searching clients');
      }
    } catch (err) {
      setError('Connection error searching clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectClient = async (client, type) => {
    const name = type === 'user' ? `${client.firstName || ''} ${client.lastName || ''}`.trim() || client.username || client.email : client.name;
    const detail = type === 'user' ? client.email : client.state;
    setSelectedClient({
      id: client.id,
      name,
      detail,
      type
    });
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await fetch(`${API_BASE}/admin/clients/${type}/${client.id}/subscription`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }).then(r => r.json());

      if (res.success && res.data) {
        setClientSub({
          plan_id: res.data.plan_id || '',
          price_override: res.data.price_override !== null ? res.data.price_override.toString() : '',
          billing_cycle: res.data.billing_cycle || 'monthly',
          status: res.data.status || 'active',
          addons: res.data.addons.map(a => ({
            addon_id: a.addon_id,
            price_override: a.price_override !== null ? a.price_override.toString() : '',
            quantity: a.quantity || 1
          }))
        });
      } else {
        // Default clean state
        setClientSub({
          plan_id: plans[0]?.id || '',
          price_override: '',
          billing_cycle: 'monthly',
          status: 'active',
          addons: []
        });
      }
    } catch (err) {
      setError('Error loading client subscription details');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAddonRow = () => {
    if (addons.length === 0) return;
    setClientSub(prev => ({ ...prev,
      addons: [...prev.addons, {
        addon_id: addons[0].id,
        price_override: '',
        quantity: 1
      }]
    }));
  };

  const handleRemoveAddonRow = index => {
    setClientSub(prev => ({ ...prev,
      addons: prev.addons.filter((_, i) => i !== index)
    }));
  };

  const handleAddonChange = (index, field, value) => {
    setClientSub(prev => {
      const updated = [...prev.addons];
      updated[index] = { ...updated[index],
        [field]: value
      };
      return { ...prev,
        addons: updated
      };
    });
  };

  const handleSaveSubscription = async e => {
    e.preventDefault();
    if (!selectedClient) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    const payload = { ...clientSub,
      price_override: clientSub.price_override.trim() === '' ? null : parseFloat(clientSub.price_override),
      addons: clientSub.addons.map(a => ({
        addon_id: a.addon_id,
        price_override: a.price_override.toString().trim() === '' ? null : parseFloat(a.price_override),
        quantity: parseInt(a.quantity, 10) || 1
      }))
    };

    try {
      const res = await fetch(`${API_BASE}/admin/clients/${selectedClient.type}/${selectedClient.id}/subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      }).then(r => r.json());

      if (res.success) {
        setSuccess('Client subscription successfully updated!');
      } else {
        setError(res.error?.message || 'Error updating subscription');
      }
    } catch (err) {
      setError('Connection error updating subscription');
    } finally {
      setLoading(false);
    }
  }; // Helper to calculate total price


  const calculateTotal = () => {
    const activePlan = plans.find(p => p.id === clientSub.plan_id);
    if (!activePlan) return '0.00';
    if (activePlan.type === 'Custom') return 'Custom';
    let basePrice = 0;

    if (clientSub.price_override.trim() !== '') {
      basePrice = parseFloat(clientSub.price_override) || 0;
    } else {
      basePrice = clientSub.billing_cycle === 'yearly' ? parseFloat(activePlan.yearly_price) / 12 : parseFloat(activePlan.monthly_price);
    }

    const addonPrice = clientSub.addons.reduce((sum, item) => {
      const matchAddon = addons.find(a => a.id === item.addon_id);
      if (!matchAddon) return sum;
      let cost = 0;

      if (item.price_override.trim() !== '') {
        cost = parseFloat(item.price_override) || 0;
      } else {
        cost = parseFloat(matchAddon.monthly_price);
      }

      return sum + cost * (parseInt(item.quantity, 10) || 1);
    }, 0);
    return (basePrice + addonPrice).toFixed(2);
  };

  return <AdminLayout>
      <Container fluid className="px-4">
        <Row className="mb-4">
          <Col>
            <h2>{t("User & Business Subscription Assignment")}</h2>
          </Col>
        </Row>

        {success && <Alert color="success">{success}</Alert>}
        {error && <Alert color="danger">{error}</Alert>}

        <Row>
          {
          /* LEFT: Client Search */
        }
          <Col md={5} className="mb-4">
            <Card>
              <CardHeader>{t("Search Client (User or Business)")}</CardHeader>
              <CardBody>
                <Form onSubmit={handleSearch} className="mb-4">
                  <FormGroup className="d-flex gap-2">
                    <Input placeholder={t("Search name, email, state...")} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
                    <Button color="primary" type="submit">
                      <FontAwesomeIcon icon={faSearch} />
                    </Button>
                  </FormGroup>
                </Form>

                {searchResults.users.length > 0 && <div className="mb-4">
                    <h5>{t("Users")}</h5>
                    <Table hover size="sm">
                      <tbody>
                        {searchResults.users.map(u => <tr key={u.id} style={{
                      cursor: 'pointer'
                    }} onClick={() => handleSelectClient(u, 'user')}>
                            <td>
                              <FontAwesomeIcon icon={faUser} className="mr-2 text-muted" />
                              {u.firstName} {u.lastName} <small className="text-muted">({u.email})</small>
                            </td>
                          </tr>)}
                      </tbody>
                    </Table>
                  </div>}

                {searchResults.businesses.length > 0 && <div>
                    <h5>{t("Businesses")}</h5>
                    <Table hover size="sm">
                      <tbody>
                        {searchResults.businesses.map(b => <tr key={b.id} style={{
                      cursor: 'pointer'
                    }} onClick={() => handleSelectClient(b, 'business')}>
                            <td>
                              <FontAwesomeIcon icon={faBuilding} className="mr-2 text-muted" />
                              {b.name} <small className="text-muted">({b.state})</small>
                            </td>
                          </tr>)}
                      </tbody>
                    </Table>
                  </div>}
              </CardBody>
            </Card>
          </Col>

          {
          /* RIGHT: Assignment Console */
        }
          <Col md={7}>
            {selectedClient ? <Card>
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <span>{t("Assign Subscription:")}<strong>{selectedClient.name}</strong> ({selectedClient.type})</span>
                  <small className="text-muted">{selectedClient.detail}</small>
                </CardHeader>
                <CardBody>
                  {loading ? <div className="text-center py-5">
                      <h4>{t("Saving subscription data...")}</h4>
                    </div> : <Form onSubmit={handleSaveSubscription}>
                      <Row>
                        <Col md={6}>
                          <FormGroup>
                            <Label>{t("Select Plan")}</Label>
                            <Input type="select" value={clientSub.plan_id} onChange={e => setClientSub({ ...clientSub,
                        plan_id: e.target.value
                      })}>
                              {plans.map(p => <option key={p.id} value={p.id}>{p.name} (${p.monthly_price !== null ? p.monthly_price : 'Custom'})</option>)}
                            </Input>
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label>{t("Billing Cycle")}</Label>
                            <Input type="select" value={clientSub.billing_cycle} onChange={e => setClientSub({ ...clientSub,
                        billing_cycle: e.target.value
                      })}>
                              <option value="monthly">{t("Monthly")}</option>
                              <option value="yearly">{t("Yearly")}</option>
                            </Input>
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <FormGroup>
                            <Label>{t("Price Override ($)")}</Label>
                            <Input placeholder={t("Leave blank for standard price")} type="number" step="0.01" value={clientSub.price_override} onChange={e => setClientSub({ ...clientSub,
                        price_override: e.target.value
                      })} />
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label>{t("Subscription Status")}</Label>
                            <Input type="select" value={clientSub.status} onChange={e => setClientSub({ ...clientSub,
                        status: e.target.value
                      })}>
                              <option value="active">{t("Active")}</option>
                              <option value="past_due">{t("Past Due")}</option>
                              <option value="canceled">{t("Canceled")}</option>
                            </Input>
                          </FormGroup>
                        </Col>
                      </Row>

                      {
                  /* Addon Assignments */
                }
                      <div className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h5>{t("\xC0 La Carte Add-on Assignments")}</h5>
                          <Button size="sm" color="success" onClick={handleAddAddonRow}>
                            <FontAwesomeIcon icon={faPlus} className="mr-1" />{t("Add Service")}</Button>
                        </div>

                        {clientSub.addons.length === 0 ? <div className="text-center py-3 border rounded text-muted">{t("No add-on services assigned.")}</div> : <Table size="sm" className="align-middle">
                            <thead>
                              <tr>
                                <th>{t("Service")}</th>
                                <th>{t("Override Price ($)")}</th>
                                <th>{t("Quantity")}</th>
                                <th>{t("Action")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {clientSub.addons.map((row, index) => <tr key={index}>
                                  <td>
                                    <Input type="select" value={row.addon_id} onChange={e => handleAddonChange(index, 'addon_id', e.target.value)}>
                                      {addons.map(a => <option key={a.id} value={a.id}>{a.name} (${a.monthly_price}{t("/mo)")}</option>)}
                                    </Input>
                                  </td>
                                  <td>
                                    <Input placeholder={t("Standard")} type="number" step="0.01" value={row.price_override} onChange={e => handleAddonChange(index, 'price_override', e.target.value)} />
                                  </td>
                                  <td style={{
                          width: 100
                        }}>
                                    <Input type="number" min="1" value={row.quantity} onChange={e => handleAddonChange(index, 'quantity', e.target.value)} />
                                  </td>
                                  <td>
                                    <Button color="danger" size="sm" onClick={() => handleRemoveAddonRow(index)}>
                                      <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                                  </td>
                                </tr>)}
                            </tbody>
                          </Table>}
                      </div>

                      {
                  /* Totals Summary */
                }
                      <Card className="mb-4  border-0">
                        <CardBody className="d-flex justify-content-between align-items-center">
                          <span className="h5 mb-0">{t("Total Computed Monthly Cost:")}</span>
                          <span className="h3 mb-0 text-success font-weight-bold">
                            {calculateTotal() === 'Custom' ? 'Custom' : `$${calculateTotal()}/mo`}
                          </span>
                        </CardBody>
                      </Card>

                      <Button color="primary" type="submit" size="lg" className="w-100">
                        <FontAwesomeIcon icon={faSave} className="mr-2" />{t("Save Subscription Assignments")}</Button>
                    </Form>}
                </CardBody>
              </Card> : <div className="text-center py-5 border rounded  text-muted">
                <h4>{t("Please select a user or business on the left to manage subscriptions.")}</h4>
              </div>}
          </Col>
        </Row>
      </Container>
    </AdminLayout>;
}